/**
 * Unified Entity Registry — THE single source of truth for every entity
 * that flows through the sync engine (UserDo + StudyGroupDo).
 *
 * Each entity declares an `authority` axis:
 *   - `'user'`  — canonical state in UserDo (default when omitted)
 *   - `'group'` — canonical state in StudyGroupDo
 *   - `'both'`  — split-canonical; reserved for the cards/deckProperties
 *                 unification in a later phase
 *
 * The two registry-builders (`framework/entity-registry.ts` and
 * `framework/group-entity-registry.ts`) filter this registry by
 * `authority` so each DO only sees the entities it dispatches.
 *
 * To add a new entity:
 *   1. Call `defineEntity({ name, type, schema, authority, ... })` here.
 *   2. Add it to the `entitySchema` object below.
 *   3. Add `schema.myEntity.def()` in `src/sync/sync.ts`.
 *
 * Server dispatch, client registries, Zustand slices, wire message
 * unions, and the inbound Zod validators all derive from this
 * automatically.
 *
 * This file is the **extension surface**. Keep it focused on entity
 * declarations and the small set of constants (DEFAULT_USER_INFO,
 * ENTITY_SCHEMA_VERSION) that travel with them.
 */

import { z } from 'zod';

import {
  creditsSummaryByKindSchema,
  DEFAULT_CREDITS_SUMMARY_BY_KIND,
} from '../../credits/types';
import { joinCardHistory } from '../framework/card-history-join';
import { mergeDeckMetaEntry } from '../framework/deck-materials-merge';
import { defineEntity } from '../framework/define-sync-entity';
import {
  mergeDeckMindmapEntry,
  mergeQuizEntry,
  mergeStudyDay,
} from '../framework/sync-merge';
import { coerceLegacyCardString } from './card-sync-fields';
import { isValidDeckMetaRow } from './deck-meta-guard';
import { CARD_FIELD_SCOPES, DECK_FIELD_SCOPES } from './field-scopes';
import type {
  SGActivityEntry,
  SGDeckMindmapEntry,
  SGDrawSettings,
  SGFolder,
  SGMember,
  SGScore,
  StudyGroupMeta,
} from './group-entities';
import type {
  SyncAnnotationsRecordEntry,
  SyncCardProps,
  SyncChatConversationEntry,
  SyncDeckMeta,
  SyncDeckMindmapEntry,
  SyncQuizProgressEntry,
} from './shared-types';
import { quizKey } from './shared-types';

/** Delimiter for the compound `(deckId, cardId)` entry key on cards. */
const CARD_KEY_DELIM = '\x01';

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

export const DEFAULT_USER_INFO = {
  role: '',
  fieldOfStudy: '',
  school: '',
  country: '',
  language: '',
} as const;

// NB: bump this version when adding or removing entities from entitySchema!
// Phase 5.3b removed linkedCardOverlay + linkedDeckOverlay — split-canonical
// reads now read shared half from the SG room store and personal half from
// the local `cards` slice directly. No overlay layer.
//
// v6 (Phase A): registry unification — the four StudyGroupDo entities
// (sgMeta, sgMembers, sgFolders, sgActivity) moved into this file with
// `authority: 'group'`. No on-wire change; wire types / storage keys /
// message names are all preserved by the explicit `messages` overrides
// each entity carries.
// v8 (studyDays): added the per-day study-counter entity (streak/heatmap/stats).
// v12 (credits): added the read-only server-authored credit-summary entity.
// v13 (credits): credit summary became a per-kind MAP (ai + transcription).
// v14 (credits): added the `oralExam` kind to the per-kind summary MAP
//   (oral exams are now a non-fungible usage wallet, not a separate counter).
// v15 (rewards): added the read-only server-authored `rewardsClaimed` entity —
//   the per-user `{ rewardId -> claimedAt date ISO }` map. Claim state moved
//   into the UserDo (atomic claim+grant) and is synced so the client computes
//   reward availability locally (catalog − claimed) without polling.
// v16 (chatConversations): added the Professor Ka agentic-chat conversation
//   record entity (one row per conversation; AI SDK messages stored inline).
// v17 (credits): added the `chat` kind to the per-kind summary MAP — Professor
//   Ka messages are now a non-fungible usage wallet (free: 10/day, paid tiers
//   uncapped), mirroring transcription/oralExam.
// v18 (widgets merge): merged the dashboard-widgets branch into the credits
//   line. Adds three personal entities — `todos` (dashboard todo widget),
//   `exploreProgress` (explore-flashka task progress) and `dashboardWidgets`
//   (per-user widget visibility/order). Bumped past v17 so every client
//   re-syncs the union (credits/rewards + widgets) on next connect.
// v19 (leaderboard merge): merged the study-group-leaderboard branch into the
//   v18 line. Adds the `sgScores` collection (centi-MP integers, see SGScore
//   type doc) and an optional `leaderboardOptOut?: boolean` field on
//   `sgMembers`. Both are purely additive — older clients ignore the new entity
//   and the new field falls through the existing `.passthrough()` on sgMembers.
//   Bumped past v18 so every client re-syncs the union (credits/rewards/widgets
//   + leaderboard) on next connect.
// v20 (calendar): added the `calendarTasks` collection — day-pinned tasks for
//   the dashboard Calendar widget (first step toward the automatic study
//   planner). Purely additive: older clients ignore the new entity and the
//   `.passthrough()` schema preserves it across round-trips.
// v21 (social): added the `socialConsent` value entity — private consent-first
//   gate state (birthYear, consentedAt) + dialog-nudge throttle counters for
//   the web social MVP. `persist: false` like `userInfo` (no op-sqlite table).
//   Purely additive: a brand-new entity has no prior timestamp on any client,
//   so sync-init ships only its tiny initial value; nothing else re-loads.
// STORY 11 (notifications): added the server-authored `notifications` value
//   entity — the global notifications inbox. `persist: false` like credits;
//   purely additive, so no version bump (matches socialConsent's rationale).
//   UserDo owns storage + resolves actor identity fresh at list-build time,
//   then broadcasts `notifications-updated` (credits pattern).
// v22 (drawing merge): merged the PDF-drawing branch. Adds the `sgDrawSettings`
//   group entity (per-group draw permission mode + allowlist, owner-writable).
//   Purely additive — older clients ignore the new entity.
// v22 (deck mindmaps): added the `deckMindmaps` record entity — ONE generated
//   mindmap per deck (study-set "Mindmaps" tab), keyed by deckName, whole-entry
//   LWW by updatedAtISO. Purely additive: older clients ignore the new entity;
//   inner mindmap/layout shapes stay opaque (z.unknown) per the policy below.
// v23 (social × mindmaps merge): the social branch and main independently
//   shipped their own "v22" (social(v21)+drawing vs drawing+mindmaps). The
//   merged schema carries the full union, so bump once more — every client
//   re-syncs the union on next connect regardless of which v22 flavor it
//   last saw.
// v23→v24 (plan × social merge): main ALSO independently shipped a "v23"
//   adding the read-only server-authored `plan` entity — the filtered mirror
//   of the tier-relevant subset of publicMetadata (status, referralStatus,
//   referralStatusExpiration, partnerStatus as the key set WAS at v24 — the
//   two referral keys were later dropped with the referral programme, without
//   a PLAN_MIRROR_VERSION bump; see `pickPlanMetadata`), replacing
//   the DO-internal `credits:substatus` string as the stored source of tier
//   truth (clients resolve the effective tier locally via the shared
//   resolveSubStatusFromMetadata). Same collision as v22: the merged schema
//   carries the full union (social + mindmaps + plan), so bump once more —
//   every client re-syncs on next connect regardless of which v23 flavor it
//   last saw.
// v25 (legacy cleanup): REMOVED the `rewardsClaimed` server-authored entity
//   (the AI-credit rewards system's claim map — the reward-granting surface
//   was removed in PR #333 and the app enforces a minimum version, so no
//   shipped client reads it anymore). Also dropped the legacy `ai` credit
//   kind from the per-kind `credits` summary MAP (an always-unlimited stub
//   kept only for pre-#333 clients). Bumped so every client re-syncs and
//   drops both from its settings cache; stale `rewards:claimed` /
//   `credits:ai:*` DO rows stay orphaned and are simply never served.
export const ENTITY_SCHEMA_VERSION = 25;

// ---------------------------------------------------------------------------
// GroupMembership type — the data shape of the `groupMemberships` entity.
// Defined explicitly because z.custom can't infer the branded shape.
// ---------------------------------------------------------------------------

/**
 * Per-user record of the study groups the current user belongs to.
 * Written by the invite-join handler (or the group creation endpoint) and
 * read by the dashboard + orchestrator to drive which StudyGroupDo rooms
 * to open. Shared-group content (members, folders, decks, cards) lives
 * in the per-group StudyGroupDo, not here.
 *
 * `cachedSummary` is a read-only snapshot populated by the dashboard's
 * HTTP `/api/study-groups/batch` response — it lets the dashboard render
 * tiles instantly without opening a WebSocket per group. Truth of record
 * for these fields is the StudyGroupDo's `meta` entity.
 */
export type GroupMembership = {
  groupId: string;
  role: 'owner' | 'editor' | 'member';
  joinedAtISO: string;
  inviteToken: string;
  lastOpenedAtISO?: string;
  lastReadAtISO?: string;
  cachedSummary?: {
    name: string;
    icon?: string;
    color?: string;
    memberCount?: number;
    deckCount?: number;
    cardCount?: number;
    lastActivityAtISO?: string;
    /**
     * Compact roster snapshot for the dashboard tile's avatar stack —
     * enough to render member photos + the presence tooltip without
     * opening a per-group WebSocket. Truth of record is the
     * StudyGroupDo's `sgMembers`; this is a derived cache, refreshed by
     * `computeGroupSummary` on every roster/profile change.
     */
    members?: Array<{
      userId: string;
      displayName?: string;
      avatarUrl?: string;
      lastOpenedAtISO?: string;
    }>;
    /**
     * Compact preview stack for the dashboard tile's folder art — the DS
     * folder-card policy (`selectFolderPreviews`) applied to the group's
     * root-level decks + folders by `computeGroupSummary`. `color` (deck
     * identity, tints the no-image page) and `folderColor` carry the RAW
     * stored colour (slug or hex); clients resolve to hex before handing
     * them to the card.
     */
    previews?: Array<{
      image?: string;
      emoji?: string;
      color?: string;
      folderColor?: string;
    }>;
  };
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

// ---------------------------------------------------------------------------
// Entity schema — declaring an entity IS registering it.
// To add a new entity, just add one key with a defineEntity() call.
//
// ── Inbound-validation policy for the post-Phase-A entities ────────────────
//
// The schemas converted away from `z.custom<T>()` below (cards,
// quizProgress, annotations, sgActivity) use Zod's DEFAULT object mode —
// "strip". Unknown top-level keys on the wire are silently dropped
// during inbound validation. That's the security gate that a smuggled
// "hack" field never reaches storage.
//
// Because the Durable Object migration has not deployed yet, every
// field that exists in the TypeScript type today is encoded as
// REQUIRED in Zod (no `.optional()` to mask drift). There is no
// historical payload shape we have to be backwards-compatible with.
//
// ── Forward-compat rule for FUTURE schema changes ──────────────────────────
//
// Once shipped, the only safe evolution is additive-only:
//
//   1. Every NEW field added after deploy MUST be `.optional()`. An
//      older client that doesn't yet emit it must still pass validation
//      — critical for offline queues that flush after an app update.
//   2. Never tighten an existing field from `.optional()` to required.
//   3. Never narrow an enum / union value set; only widen.
//
// Nested complex types (CardAction, SyncChatMessage, SRAlgoInfo,
// SyncQuizProgressData, SyncPdfAnnotation, etc.) are typed loosely as
// `z.unknown()` / `z.array(z.unknown())`. The goal is to gate the
// entity-level shape and let nested validation come incrementally
// without a wire-format change. Tightening a nested type later is safe
// as long as production data conforms.
//
// NOTE: the older entities below (folders, settings, userInfo,
// groupMemberships, deckProperties, sgMeta, sgMembers, sgFolders) still
// use `.passthrough()`. Flipping them to strip mode is a separate
// follow-up — see the comment on `folders.schema` for the historical
// reason.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// studyDays — per-day study activity counters (streaks, heatmap, stats).
//
// Personal (authority defaults to 'user'), so it is independent of cold-deck
// archival. One entry per LOCAL day; `sessions` is keyed by auth session id →
// an OPEN bag of named counters. Totals are derived by summing across sessions;
// the merge takes per-session/per-field `max` (see `mergeStudyDay`), which is
// offline- and multi-device-safe. The type is inferred from the schema — the
// single source of truth — so there is no hand-written interface to drift.
// ---------------------------------------------------------------------------
const studyDaySchema = z.object({
  dateKey: z.string(),
  sessions: z.record(z.record(z.number())),
  updatedAtISO: z.string(),
});
export type StudyDay = z.infer<typeof studyDaySchema>;
export type CounterBag = StudyDay['sessions'][string];

// ---------------------------------------------------------------------------
// todos — dashboard "Todo List" widget entries. The recurrence shape is
// declared as an explicit TS union so `SyncTodo` keeps the right type
// without TS losing it through z.discriminatedUnion inference.
// ---------------------------------------------------------------------------
export type SyncTodoRecurrence =
  | { type: 'once' }
  | { type: 'daily' }
  | { type: 'weekly'; weekday: number }
  | { type: 'biweekly' }
  | { type: 'monthly'; dayOfMonth: number }
  | { type: 'yearly' };

export type SyncTodoMention = {
  id: string;
  kind: 'deck' | 'folder';
  label: string;
};

export type SyncTodo = {
  id: string;
  title: string;
  mentions?: SyncTodoMention[];
  recurrence: SyncTodoRecurrence;
  /** Local day key 'YYYY-MM-DD' this to-do is planned for, if any. */
  plannedDateISO?: string;
  lastCompletedISO?: string;
  sortKey?: string;
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

const todoSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    mentions: z
      .array(
        z.object({
          id: z.string(),
          kind: z.enum(['deck', 'folder']),
          label: z.string(),
        }),
      )
      .optional(),
    recurrence: z.discriminatedUnion('type', [
      z.object({ type: z.literal('once') }),
      z.object({ type: z.literal('daily') }),
      z.object({ type: z.literal('weekly'), weekday: z.number().min(0).max(6) }),
      z.object({ type: z.literal('biweekly') }),
      z.object({
        type: z.literal('monthly'),
        dayOfMonth: z.number().min(1).max(31),
      }),
      z.object({ type: z.literal('yearly') }),
    ]),
    plannedDateISO: z.string().optional(),
    lastCompletedISO: z.string().optional(),
    sortKey: z.string().optional(),
    updatedAtISO: z.string().optional(),
    deletedTimeISO: z.string().nullable().optional(),
  })
  // `.passthrough()` preserves unknown fields so a newer client's schema
  // additions survive a round-trip through an older client. Without it, Zod
  // strips unknown keys during inbound validation and a follow-up push from
  // the older client overwrites the server's canonical row with the stripped
  // shape — see the `folders` entity and SYNC_ENGINE_BUGS.md "Schema drift
  // across entity types". Mirrors every other collection here.
  .passthrough() as unknown as z.ZodType<SyncTodo>;

// ---------------------------------------------------------------------------
// calendarTasks — dashboard "Calendar" widget entries. A task pinned to a
// specific day (`dateISO`), with optional inline @mentions of decks/folders
// (same shape as todos). Distinct from `todos` (which are recurrence-based)
// because calendar tasks are day-scheduled — the foundation the automatic
// study planner will write into.
// ---------------------------------------------------------------------------
export type CalendarTask = {
  id: string;
  dateISO: string; // local day key 'YYYY-MM-DD'
  text: string;
  done?: boolean;
  mentions?: SyncTodoMention[];
  sortKey?: string;
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

const calendarTaskSchema = z
  .object({
    id: z.string(),
    dateISO: z.string(),
    text: z.string(),
    done: z.boolean().optional(),
    mentions: z
      .array(
        z.object({
          id: z.string(),
          kind: z.enum(['deck', 'folder']),
          label: z.string(),
        }),
      )
      .optional(),
    sortKey: z.string().optional(),
    updatedAtISO: z.string().optional(),
    deletedTimeISO: z.string().nullable().optional(),
  })
  // `.passthrough()` — see todoSchema above (schema-drift protection).
  .passthrough() as unknown as z.ZodType<CalendarTask>;

// ---------------------------------------------------------------------------
// notifications (Story 11) — global notifications inbox.
//
// The synced entity is the ANNOTATED list the client renders (actor identity
// resolved fresh at build-time against `social_profiles`), plus a single
// last-seen watermark. Unseen count is derived on the client (items newer than
// the watermark). Server-authored (UserDo is the sole writer via the
// `/internal/notifications/push` + `/internal/notifications/mark-seen`
// endpoints — clients never push this entity).
//
// Four kinds today: `new_follower`, `university_added`, `deck_opened` and
// `group_invite`. The discriminated union stays trivially extensible; an older
// client that meets an unknown type DROPS the row (see
// `resolveSocialNotificationMessage`) rather than rendering an empty card.
// `updatedAtISO` is carried inside the payload
// so the wire message can broadcast the full snapshot (mirrors the credits
// entity's value-doc pattern).
// ---------------------------------------------------------------------------
const notificationItemSchema = z.discriminatedUnion('type', [
  z
    .object({
      id: z.string(),
      type: z.literal('new_follower'),
      actorId: z.string(),
      createdAtISO: z.string(),
      actorHandle: z.string(),
      actorDisplayName: z.string(),
      actorAvatarUrl: z.string().optional(),
      /** ISO timestamp of the actor's last snapshot push (`social_profiles.last_active_at`).
       *  Feeds the avatar tooltip's activity dot on web (same bucketing as the SG leaderboard).
       *  Absent for actors who never pushed a snapshot yet. */
      actorLastActive: z.string().optional(),
      followedBack: z.boolean().optional(),
    })
    .passthrough(),
  // "Your school is now on Flashka" — pushed by the manual triage loop after a
  // user's "Can't find your school?" submission gets promoted into the
  // universities dataset. Actorless; `universityName` is resolved fresh from
  // the `universities` table at blob build-time (mirrors actor identity).
  z
    .object({
      id: z.string(),
      type: z.literal('university_added'),
      universityId: z.string(),
      universityName: z.string(),
      createdAtISO: z.string(),
    })
    .passthrough(),
  // "You shared {Set} with @someone" — the recipient of a link-shared Set
  // opened (imported) it and the owner does not follow them yet. Exists only
  // for its Follow CTA: the blob build DROPS the item once the owner follows
  // the actor, so this row is never a "they opened it again" log.
  // `deckName` is snapshotted at push time (deck names live in the owner's
  // UserDo, not in Postgres); actor identity is resolved fresh like
  // `new_follower`.
  z
    .object({
      id: z.string(),
      type: z.literal('deck_opened'),
      /** The RECIPIENT who opened the link — the person the owner may follow. */
      actorId: z.string(),
      /** The OWNER's deck id (the notification lands in the owner's inbox). */
      deckId: z.string(),
      deckName: z.string(),
      createdAtISO: z.string(),
      actorHandle: z.string(),
      actorDisplayName: z.string(),
      actorAvatarUrl: z.string().optional(),
      actorLastActive: z.string().optional(),
      /** Owner → actor. Flips to true once the owner follows from the row —
       *  the row stays, its Follow button just becomes "Following". */
      viewerFollows: z.boolean().optional(),
    })
    .passthrough(),
  // "Join {group}" — a member of a study group sent an in-app invite. One row
  // per group per recipient regardless of who invited (StudyGroupDo keeps an
  // `invite:{userId}` record, so a person is never invited twice to the same
  // group). `groupName` + `inviteToken` are snapshotted at push time: group
  // metadata lives in the StudyGroupDo, not in Postgres.
  z
    .object({
      id: z.string(),
      type: z.literal('group_invite'),
      /** The inviter. */
      actorId: z.string(),
      groupId: z.string(),
      groupName: z.string(),
      inviteToken: z.string(),
      createdAtISO: z.string(),
      actorHandle: z.string(),
      actorDisplayName: z.string(),
      actorAvatarUrl: z.string().optional(),
      actorLastActive: z.string().optional(),
    })
    .passthrough(),
]);
export type SyncNotificationItem = z.infer<typeof notificationItemSchema>;

const notificationsPayloadSchema = z
  .object({
    items: z.array(notificationItemSchema),
    seenWatermarkISO: z.string().nullable(),
  })
  .passthrough();
export type SyncNotifications = z.infer<typeof notificationsPayloadSchema>;

const DEFAULT_NOTIFICATIONS: SyncNotifications = {
  items: [],
  seenWatermarkISO: null,
};

// ---------------------------------------------------------------------------
// inboxMessages — the notifications screen's PLATFORM rows: what an automation
// or a campaign with "Show in notifications screen" on put in this person's
// list (the push service's `notif_inbox`, flashka-platform
// apps/auth/worker/src/push/inbox.ts). The UserDo reads the person's live rows
// from the push service on app open (party/server/user/inbox-messages.ts) and
// holds them here; clients merge them into the bell as "From Ka" rows.
//
// A SEPARATE entity from `notifications`, on purpose: that payload is a strict
// discriminated union an older client validates whole, so a new row kind
// inside it could blank an old app's bell. An entity an old client does not
// know is simply never asked for. Each row arrives worded in every language
// its template has; the client picks the app's.
// ---------------------------------------------------------------------------
const inboxMessageSchema = z
  .object({
    /** `inbox:<pushId>` — what the client reports its events under. */
    id: z.string(),
    category: z.string(),
    automationId: z.string().optional(),
    campaignId: z.string().optional(),
    variantId: z.string().optional(),
    content: z.object({
      defaultLocale: z.string(),
      variants: z.record(z.string(), z.object({ title: z.string(), body: z.string() })),
    }),
    /** A push route target (`shared/src/push/types.ts` `PushRouteTarget`), as stored. */
    route: z.record(z.string(), z.unknown()),
    countsInBadge: z.boolean(),
    /** Ka's expression, by id, for the row's avatar. */
    expression: z.string().optional(),
    createdAtISO: z.string(),
    expiresAtISO: z.string(),
    /**
     * When this person's UserDo first held the row (it pulls on app open, so
     * minutes or days after `createdAtISO`). A row is unread until the bell
     * was opened AFTER this — not after its creation, which an earlier
     * opening may already have passed. Absent on rows stored before it.
     */
    receivedAtISO: z.string().optional(),
  })
  .passthrough();
export type SyncInboxMessage = z.infer<typeof inboxMessageSchema>;

const inboxMessagesPayloadSchema = z
  .object({ items: z.array(inboxMessageSchema) })
  .passthrough();
export type SyncInboxMessages = z.infer<typeof inboxMessagesPayloadSchema>;

// notificationsRead — PER-ITEM read marks for the notification centre.
//
// The inbox's own read model is two CURSORS: `seenWatermarkISO` above, and each
// group's `SGMember.lastReadAtISO`. A cursor can only ever say "everything up
// to here is read", which is the right shape for "mark all as read" and the
// wrong shape for dismissing ONE row — stamping the cursor to a single row's
// time would silently mark every older row read with it.
//
// So per-item marks live here instead, as an explicit id set that is UNIONed
// with the cursors when a row's `read` flag is derived (see
// `dashboard/notifications/feed.ts`). Ids are the merged feed's namespaced
// entry ids (`s:<id>` / `g:<groupId>:<id>`), so social and study-group rows
// share one namespace and one write path.
//
// CLIENT-authored (no `serverAuthored`): the UserDo persists and re-broadcasts
// it through the generic entity dispatch, so read marks follow the account to
// every device instead of being a per-install preference. `persist: false`
// mirrors the `notifications` entity it annotates — the two arrive together on
// sync-init, so a cold start never renders rows as unread that the user has
// already dismissed on another device.
//
// Insertion-ordered and trimmed from the FRONT at the cap: the oldest marks
// fall off first, and they are the ones whose rows have already aged out of the
// server's own 50-row inbox cap. The two sets are mutually exclusive — writing
// one id removes it from the other, so a row is never both.
// ---------------------------------------------------------------------------
export const NOTIFICATIONS_READ_MAX_IDS = 200;

const notificationsReadSchema = z
  .object({
    ids: z.array(z.string()),
    /**
     * Rows forced back to UNREAD. Needed because "read" has three sources, not
     * two: a cursor can already cover a row, and no amount of removing it from
     * `ids` would undo that. Marking a row unread is therefore an explicit
     * override that wins over both the cursor and `ids`.
     */
    unreadIds: z.array(z.string()).optional(),
    updatedAtISO: z.string().optional(),
  })
  .passthrough();
export type SyncNotificationsRead = z.infer<typeof notificationsReadSchema>;

// ---------------------------------------------------------------------------
// studyReminders — the account's "remind me to study" switch, and the zone
// the reminder is timed in.
//
// The switch the user already knows: mobile Settings' daily study reminder.
// For someone with an active study plan, the reminder stops being a fixed
// local notification on each phone and becomes the plan-aware push the user's
// UserDo sends (`party/server/user/study-plan-reminder.ts`, rules in
// `study-plan/reminder.ts`) — one per account, on every device — so the switch
// and the zone have to reach the server. CLIENT-authored; persisted and
// re-broadcast through the generic entity dispatch.
//
// `timeZone` is an IANA name (`Europe/Berlin`), never an offset: an offset is
// wrong for half the year wherever clocks change. Clients refresh it when it
// differs from the device's, so travel moves the reminder with the person.
// `enabled` absent ⇒ never written ⇒ no server reminders (the device-local
// reminder keeps working for that account exactly as before).
// ---------------------------------------------------------------------------
const studyRemindersSchema = z
  .object({
    enabled: z.boolean().optional(),
    timeZone: z.string().max(64).optional(),
    // "Quiz me in notifications" (RE2): a question from the person's own cards
    // on the lock screen, now and then, when they have been away. A consent
    // to what shows there, so absent ⇒ off, never pre-ticked. Additive.
    quizInNotifications: z.boolean().optional(),
    updatedAtISO: z.string().optional(),
  })
  .passthrough();
export type SyncStudyReminders = z.infer<typeof studyRemindersSchema>;

// ---------------------------------------------------------------------------
// plan — filtered mirror of the tier-relevant `publicMetadata` keys.
// NOT a resolved tier: clients derive the effective tier with the shared
// `resolveSubStatusFromMetadata`. Values are plain strings (not the tier enum)
// so a new tier added server-side never fails older clients' validation.
//
// `referralStatus`/`referralStatusExpiration` used to live here. They went with
// the referral programme; `.passthrough()` is what lets an already-stored
// mirror that still carries them parse unchanged (see `pickPlanMetadata` for
// why PLAN_MIRROR_VERSION was deliberately not bumped).
// ---------------------------------------------------------------------------
const planSchema = z
  .object({
    status: z.string().optional(),
    partnerStatus: z.string().optional(),
  })
  .passthrough();
export type PlanMetadata = z.infer<typeof planSchema>;

/**
 * Version of the mirrored key subset. Bump when `pickPlanMetadata` changes:
 * writers stamp it into the stored mirror doc, and the server treats a stale
 * stamp as "nothing stored" → re-polls the auth worker.
 *
 * v2: added `partnerStatus` (partner grants, e.g. Revolut).
 */
export const PLAN_MIRROR_VERSION = 2;

/**
 * ONE `DeckSource` entry (`SyncDeckMeta.sources[]`) — a deck's source overview.
 *
 * Named (rather than inlined into `deckProperties` below) because the Vercel →
 * UserDo bridge validates the `/internal/deck-sources` RPC response against it
 * (`server/routes/user-do-bridge.ts`), the same way the credits RPCs validate
 * against their shared wire schemas. One definition means a field added here
 * cannot be silently dropped on the way to `get-ai-deck-source`.
 */
export const deckSourceSchema = z
  .object({
    kind: z.enum(['pdf', 'transcription', 'photo', 'apkg', 'gpt', 'manual', 'text']),
    sourceId: z.string(),
    sourceLabel: z.string().optional(),
    // The user's own name for the source (⋯ → Rename). Additive:
    // absent until renamed, passed through by older clients.
    title: z.string().optional(),
    addedAtISO: z.string(),
    // Materials-order key (drag a tab / tile to reorder). Additive:
    // absent until first reordered, passed through by older clients.
    sortKey: z.string().optional(),
    // Per-entry clocks for the `sources` union (edit / place).
    // Additive: absent on entries older clients write.
    updatedAtISO: z.string().optional(),
    movedAtISO: z.string().optional(),
    // Undo's restore clock — the only edit that outranks the
    // entry's removal. Additive.
    restoredAtISO: z.string().optional(),
    // The document's measured length (`DeckSourceTextLength`).
    // Additive: absent until a device with the text recorded it;
    // older clients pass it through (`.passthrough()` below).
    textLength: z
      .object({
        totalChars: z.number(),
        paragraphCount: z.number(),
        pagesKey: z.string(),
      })
      .passthrough()
      .optional(),
    // Additive provenance (`DeckSourceOrigin`). Deliberately an
    // OPEN string here — a closed enum would make the next origin
    // value a breaking change for older clients, exactly the trap
    // `kind` already is.
    origin: z.string().optional(),
    // `DeckSourceLibraryRef` — additive, so older clients pass
    // it through and show the chapter as the PDF it also is.
    library: z
      .object({ slug: z.string(), unit: z.string(), version: z.string() })
      .passthrough()
      .optional(),
    link: z
      .object({ url: z.string(), title: z.string().optional() })
      .passthrough()
      .optional(),
    pdfInfo: z
      .object({
        filePath: z.string(),
        transcriptFilePath: z.string().optional(),
        pages: z.array(z.number()).optional(),
        transcriptionId: z.string().optional(),
        video: z
          .object({
            provider: z.literal('youtube'),
            videoId: z.string(),
            url: z.string(),
            title: z.string().optional(),
            durationSec: z.number().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

export const entitySchema = {
  folders: defineEntity({
    name: 'folders',
    type: 'collection',
    primaryKey: 'id',
    // `.passthrough()` preserves unknown fields so a newer client's
    // schema additions survive a round-trip through an older client.
    // Without it, Zod strips unknown keys during inbound validation and
    // a follow-up push from the older client overwrites the server's
    // canonical row with the stripped shape. See SYNC_ENGINE_BUGS.md
    // "Schema drift across entity types".
    schema: z
      .object({
        id: z.string(),
        name: z.string(),
        color: z.string(),
        // Optional: legacy/migrated folders predate the per-folder language
        // field, so requiring it fails `folders-push` validation for any
        // account migrated from the old Supabase shape ("Required").
        language: z.string().optional(),
        // Nullable: clients store this as `string | null` (mobile SQLite
        // column, web local row), so a non-archived folder pushes
        // `archivedTimeISO: null`. `z.string().optional()` only accepted
        // `undefined`, which jammed `folders-push` ("Expected string,
        // received null"). Mirrors the sibling `deletedTimeISO` field.
        archivedTimeISO: z.string().nullable().optional(),
        parentFolder: z.string().nullable().optional(),
        orderIndex: z.number().optional(),
        sortKey: z.string().optional(),
        updatedAtISO: z.string().optional(),
        deletedTimeISO: z.string().nullable().optional(),
      })
      .passthrough(),
  }),

  // Dashboard "Todo List" widget entries.
  // Free-form text with optional inline @mentions of decks/folders.
  todos: defineEntity<SyncTodo, 'todos'>({
    name: 'todos',
    type: 'collection',
    primaryKey: 'id',
    schema: todoSchema,
  }),

  // Dashboard "Calendar" widget entries — tasks pinned to a specific day.
  calendarTasks: defineEntity<CalendarTask, 'calendarTasks'>({
    name: 'calendarTasks',
    type: 'collection',
    primaryKey: 'id',
    schema: calendarTaskSchema,
  }),

  settings: defineEntity({
    name: 'globalSrSettings',
    type: 'value',
    schema: z
      .object({
        preset: z.enum(['default', 'medical', 'custom']),
        overrides: z.record(z.unknown()).optional(),
      })
      .passthrough(),
    initial: { preset: 'default' as const },
    tsKey: '__settings',
    storageKey: 'settings',
    diffKey: 'settings',
    messages: {
      push: { type: 'settings-push', dataKey: 'settings' },
      broadcast: { type: 'settings-updated', dataKey: 'settings' },
    },
  }),

  userInfo: defineEntity({
    name: 'userInfo',
    type: 'value',
    schema: z
      .object({
        role: z.union([
          z.literal('student'),
          z.literal('teacher'),
          z.literal('other'),
          z.literal(''),
        ]),
        fieldOfStudy: z.string(),
        school: z.string(),
        country: z.string(),
        language: z.string(),
        aiLanguage: z.string().optional(),
        origin: z.string().optional(),
        features: z.array(z.string()).optional(),
        // New-Onboarding (Jul 26) fields — all optional so old clients'
        // writes (which omit them) merge cleanly.
        name: z.string().optional(),
        universityId: z.string().optional(),
        studyGoal: z.enum(['exam', 'casual']).optional(),
        examDateISO: z.string().optional(),
        studyWays: z.array(z.string()).optional(),
        // Explicit completion marker: the new flow collects a DIFFERENT field
        // set than the legacy role/country/fieldOfStudy/language-derived
        // "done" predicate, so completion is stamped instead of inferred.
        onboardingCompletedAtISO: z.string().optional(),
        // When the last live lecture recording started — the time of day the
        // "next lecture" push (A4) lands just before, the day after. Additive.
        lastLectureStartedAtISO: z.string().optional(),
        // When onboarding's upload step was skipped ("I don't have a doc
        // ready"): who the "upload your own material" push (A2) is for.
        // Additive; absent on anyone who uploaded or onboarded before it.
        onboardingUploadSkippedAtISO: z.string().optional(),
      })
      .passthrough(),
    initial: DEFAULT_USER_INFO as {
      role: '' | 'student' | 'teacher' | 'other';
      fieldOfStudy: string;
      school: string;
      country: string;
      language: string;
      aiLanguage?: string;
      origin?: string;
      features?: string[];
      name?: string;
      universityId?: string;
      studyGoal?: 'exam' | 'casual';
      examDateISO?: string;
      studyWays?: string[];
      onboardingCompletedAtISO?: string;
      lastLectureStartedAtISO?: string;
      onboardingUploadSkippedAtISO?: string;
    },
    // No op-sqlite typed table — userInfo isn't persisted to a v2 table
    // (the value flows through the engine's settings row or in-memory cache).
    storage: { persist: false },
  }),

  // Per-user explore-flashka task progress — a flat map of taskId -> done.
  // Value entity so the whole map syncs as one unit; mirrors the mobile
  // dashboard's explore-flashka widget and roams across devices.
  exploreProgress: defineEntity({
    name: 'exploreProgress',
    type: 'value',
    schema: z.record(z.boolean()),
    initial: {} as Record<string, boolean>,
  }),

  // FTUE v2 guided first session (`shared/src/ftue/machine.ts`): which
  // methods it runs, where the student is, "Finish later" — so the session
  // resumes on the next open, on any device. Client-authored, one small
  // value; `{}` = no session. Loose record on purpose: the machine's own
  // reader (`readFtueSession`) validates the version, so a future shape never
  // breaks an older client's sync. `persist: false`: arrives on sync-init.
  ftueSession: defineEntity({
    name: 'ftueSession',
    type: 'value',
    schema: z.record(z.unknown()),
    initial: {} as Record<string, unknown>,
    storage: { persist: false },
  }),

  // Coach marks the account has met — `{ <markId>: true, 'group:<group>':
  // true }` (`shared/coach-marks/seen.ts`). A tour seen on one device or
  // browser stays seen on every other (QA 2026-10-09: "they keep appearing
  // after a refresh / in other sessions"). Each client keeps its own
  // device mirror (web localStorage, app MMKV) for the synchronous first
  // read and UNIONS it with this value both ways, so a write that loses the
  // whole-value LWW race is re-pushed by the device that still holds it — a
  // seen mark is never lost, only ever added. `persist: false` like
  // `studyReminders`: arrives on sync-init, no client table of its own.
  coachMarksSeen: defineEntity({
    name: 'coachMarksSeen',
    type: 'value',
    schema: z.record(z.boolean()),
    initial: {} as Record<string, boolean>,
    storage: { persist: false },
  }),

  // Per-user dashboard HOME settings so a user's configuration survives
  // sign-out / sign-in and roams across devices. The entity name is a
  // historical misnomer (it maps to the dashboard_widgets_v2 table —
  // NEVER rename it, that forks the table and drops every user's config):
  // besides widget visibility/order it now carries the content-section
  // enablement + collapse state added with the shared dashboard-settings
  // model (`@flashka/shared/dashboard`). Purely additive + .passthrough(),
  // so old clients round-trip the new fields untouched.
  dashboardWidgets: defineEntity({
    name: 'dashboardWidgets',
    type: 'value',
    schema: z
      .object({
        widgets: z.array(
          z.object({
            id: z.string(),
            enabled: z.boolean(),
          }),
        ),
        sections: z
          .array(
            z.object({
              id: z.string(),
              enabled: z.boolean(),
            }),
          )
          .optional(),
        collapsedSections: z.array(z.string()).optional(),
      })
      .passthrough(),
    initial: { widgets: [] } as {
      widgets: Array<{ id: string; enabled: boolean }>;
      sections?: Array<{ id: string; enabled: boolean }>;
      collapsedSections?: string[];
    },
  }),

  // Per-user PRIVATE social state — the consent-first gate for the social
  // layer. This is the source of truth for `birthYear` (never a boolean/age;
  // eligibility = currentYear - birthYear >= 16, computed at render time) and
  // `consentedAt` (set only when the public `social_profiles` row is
  // actually created). Also carries the dialog-nudge throttle counters:
  // the age-gate PROACTIVE-modal throttle (`ageGateShownCount` /
  // `ageGateLastShownDay`) and the shared Complete-Profile throttle across
  // BOTH its triggers (`completeProfileShownCount` / `completeProfileLastShownDay`).
  // Day strings are LOCAL days. `persist: false` (like `userInfo`) — held in
  // the settings row / in-memory cache, no dedicated op-sqlite `_v2` table;
  // it still syncs + roams across devices and persists locally on web so the
  // gate never flashes on reload. Nothing here is ever public (Postgres has no
  // age/birthYear column, ever). Mobile reads it in a later story.
  socialConsent: defineEntity({
    name: 'socialConsent',
    type: 'value',
    schema: z
      .object({
        birthYear: z.number().nullable(),
        consentedAt: z.string().nullable(),
        ageGateShownCount: z.number(),
        ageGateLastShownDay: z.string().nullable(),
        completeProfileShownCount: z.number(),
        completeProfileLastShownDay: z.string().nullable(),
        // Client `Date.getTimezoneOffset()` (minutes behind UTC). Written at
        // Dialog-1 Confirm and refreshed on app load when missing/changed, so
        // the Story-6 retro `studyDays` backfill can bucket history by the
        // user's LOCAL day. Optional — absent ⇒ the backfill falls back to UTC
        // (0) rather than waiting. Never public (Postgres has no such column).
        tzOffsetMinutes: z.number().optional(),
      })
      .passthrough(),
    initial: {
      birthYear: null,
      consentedAt: null,
      ageGateShownCount: 0,
      ageGateLastShownDay: null,
      completeProfileShownCount: 0,
      completeProfileLastShownDay: null,
    } as {
      birthYear: number | null;
      consentedAt: string | null;
      ageGateShownCount: number;
      ageGateLastShownDay: string | null;
      completeProfileShownCount: number;
      completeProfileLastShownDay: string | null;
      tzOffsetMinutes?: number;
    },
    storage: { persist: false },
  }),

  // Read-only credit rollup, one CreditsSummary per kind (ai/transcription/…).
  // `serverAuthored`: the UserDo is the sole writer (via /internal/credits/apply);
  // clients only read `sync.credits.use()`. `persist: false` — held in the
  // in-memory settings cache, never a v2 table.
  credits: defineEntity({
    name: 'credits',
    type: 'value',
    schema: creditsSummaryByKindSchema,
    initial: DEFAULT_CREDITS_SUMMARY_BY_KIND,
    tsKey: '__credits',
    storageKey: 'credits:summary',
    diffKey: 'credits',
    settingsKey: 'credits',
    serverAuthored: true,
    messages: {
      push: { type: 'credits-push', dataKey: 'credits' },
      broadcast: { type: 'credits-updated', dataKey: 'credits' },
    },
    storage: { persist: false },
  }),

  // Story 11 — global notifications inbox. Server-authored value entity
  // (UserDo is the sole writer via /internal/notifications/push + /mark-seen).
  // Payload = the ANNOTATED list the client renders (actor identity resolved
  // fresh from social_profiles at build-time) + a single seen-watermark ISO.
  // Unseen count is derived client-side (items newer than the watermark).
  // `persist: false` — mirrors credits; inbox is short (≤50 rows) and cheap to
  // re-hydrate at sync-init.
  notifications: defineEntity({
    name: 'notifications',
    type: 'value',
    schema: notificationsPayloadSchema,
    initial: DEFAULT_NOTIFICATIONS,
    tsKey: '__notifications',
    storageKey: 'notifications:blob',
    diffKey: 'notifications',
    settingsKey: 'notifications',
    serverAuthored: true,
    messages: {
      push: { type: 'notifications-push', dataKey: 'notifications' },
      broadcast: { type: 'notifications-updated', dataKey: 'notifications' },
    },
    storage: { persist: false },
  }),

  // The notifications screen's platform rows — see the schema docblock above.
  // Server-authored (the UserDo is the sole writer, from the push service),
  // `persist: false` like `notifications`: a handful of rows, re-read on connect.
  inboxMessages: defineEntity({
    name: 'inboxMessages',
    type: 'value',
    schema: inboxMessagesPayloadSchema,
    initial: { items: [] } as SyncInboxMessages,
    tsKey: '__inboxMessages',
    storageKey: 'inboxMessages:blob',
    diffKey: 'inboxMessages',
    settingsKey: 'inboxMessages',
    serverAuthored: true,
    messages: {
      push: { type: 'inbox-messages-push', dataKey: 'inboxMessages' },
      broadcast: { type: 'inbox-messages-updated', dataKey: 'inboxMessages' },
    },
    storage: { persist: false },
  }),

  // Per-item read marks for the notification centre — see the schema docblock
  // above for why these are an id SET rather than another cursor.
  notificationsRead: defineEntity({
    name: 'notificationsRead',
    type: 'value',
    schema: notificationsReadSchema,
    initial: { ids: [] } as SyncNotificationsRead,
    storage: { persist: false },
  }),

  // The study-reminder switch + the zone it is timed in — see the schema
  // docblock above.
  studyReminders: defineEntity({
    name: 'studyReminders',
    type: 'value',
    schema: studyRemindersSchema,
    initial: {} as SyncStudyReminders,
    // Like `socialConsent`: arrives on sync-init, no client table of its own.
    storage: { persist: false },
  }),

  // Filtered mirror of publicMetadata tier keys — see planSchema above.
  // `serverAuthored`: the UserDo is the sole writer (ensureSubStatus poll +
  // the auth worker's sync-substatus push); clients only read. `initial` is undefined on
  // purpose: "not yet synced" must never resolve to 'basic'.
  plan: defineEntity({
    name: 'plan',
    type: 'value',
    schema: planSchema,
    initial: undefined as unknown as PlanMetadata,
    storageKey: 'credits:plan',
    serverAuthored: true,
    storage: { persist: false },
  }),

  // Top-level fields validated; `attempts[]` stays opaque
  // (`SyncQuizProgressData` is a wide nested shape — see policy block
  // above). Future fields MUST be `.optional()`.
  quizProgress: defineEntity({
    name: 'quizProgress',
    type: 'record',
    schema: z.object({
      deckId: z.string(),
      quizTs: z.string(),
      attempts: z.array(z.unknown()),
      updatedAtISO: z.string(),
      deletedTimeISO: z.string().optional(),
      isFolderQuiz: z.boolean().optional(),
      userId: z.string().optional(),
      sharedAt: z.string().optional(),
      shareCode: z.string().optional(),
      // Discriminates non-quiz entries (oral exams, case studies) from regular
      // quizzes. MUST be a recognized schema field — otherwise Zod strip-mode
      // would drop it on the wire and the discriminator would never
      // persist/sync. Enum keeps forward-compat (can widen later, never narrow).
      examKind: z.enum(['oral', 'caseStudy']).optional(),
      restartOf: z.string().optional(),
    }) as unknown as z.ZodType<SyncQuizProgressEntry>,
    entryKey: (e: SyncQuizProgressEntry) => quizKey(e.deckId, e.quizTs),
    doPrefix: 'quiz:',
    tsKeyPrefix: '__quiz:',
    mergeEntry: mergeQuizEntry,
    deleteType: 'quiz-delete',
    deleteBroadcastType: 'quiz-deleted',
    decomposeStorageKey: (key: string) => {
      // deckIds are UUIDs, so "::" only appears as the delimiter — but
      // keep lastIndexOf for safety in case an older row leaks through.
      const sep = key.lastIndexOf('::');
      return sep === -1 ? key : [key.slice(0, sep), key.slice(sep + 2)];
    },
    messages: {
      push: { type: 'quiz-push', dataKey: 'quizProgress' },
      broadcast: { type: 'quiz-updated', dataKey: 'quizProgress' },
    },
    storage: { pk: ['deckId', 'quizTs'] },
  }),

  // Per-day study activity counters (streak / heatmap / stats). See the
  // `studyDaySchema` block above. Counters are an OPEN set — add a new one via
  // a one-line emitter in `src/sync-client/sync.ts`; schema + merge are
  // field-agnostic and need no change.
  studyDays: defineEntity({
    name: 'studyDays',
    type: 'record',
    schema: studyDaySchema,
    entryKey: (e: StudyDay) => e.dateKey,
    doPrefix: 'studyday:',
    tsKeyPrefix: '__studyday:',
    mergeEntry: mergeStudyDay,
    messages: {
      push: { type: 'studyday-push', dataKey: 'studyDays' },
      broadcast: { type: 'studyday-updated', dataKey: 'studyDays' },
    },
    storage: { pk: ['dateKey'] },
  }),

  // Per-deck PDF annotations (highlights, explanations, askKa).
  // One entry per deck, keyed by deckName. Inner annotation arrays
  // stay opaque (`SyncPdfAnnotation` / `SyncAskKaAnnotation` are wide
  // nested shapes — see policy block above). Future fields MUST be
  // `.optional()`.
  annotations: defineEntity({
    name: 'annotations',
    type: 'record',
    schema: z.object({
      deckName: z.string(),
      annotations: z.object({
        notes: z.array(z.unknown()),
        explanations: z.array(z.unknown()),
        askKa: z.array(z.unknown()),
      }),
      updatedAtISO: z.string(),
    }) as unknown as z.ZodType<SyncAnnotationsRecordEntry>,
    entryKey: (e: SyncAnnotationsRecordEntry) => e.deckName,
    doPrefix: 'annotations:',
    tsKeyPrefix: '__annotations:',
    deleteType: 'annotations-delete',
    deleteBroadcastType: 'annotations-deleted',
    storage: { pk: ['deckName'] },
  }),

  // The study-set page's generated mindmap — ONE per deck, keyed by deckName
  // (a single record with the whole mindmap state, like flashcards' deck
  // record; NOT a Yjs doc like notes). `mindmap` is the logical tree the
  // model authored (plus user structure edits); `layout` is the user-arranged
  // rendered state saved from the canvas. Inner shapes stay opaque
  // (`z.unknown()` — wide nested UI-owned shapes, see policy block above);
  // whole-entry LWW by `updatedAtISO` is enough (single-user editing).
  // Future fields MUST be `.optional()`.
  deckMindmaps: defineEntity({
    name: 'deckMindmaps',
    type: 'record',
    schema: z.object({
      deckName: z.string(),
      mindmap: z.object({
        title: z.string().optional(),
        nodes: z.array(z.unknown()),
      }),
      layout: z.unknown().optional(),
      // The infinite-canvas graph (v2). Optional + opaque: older clients
      // ignore it and keep drawing the `mindmap`/`layout` projection.
      graph: z.unknown().optional(),
      // The v1 part is newer than `graph` (an older client saved it): see
      // `mergeDeckMindmapEntry`.
      graphStale: z.literal(true).optional(),
      updatedAtISO: z.string(),
    }) as unknown as z.ZodType<SyncDeckMindmapEntry>,
    // LWW, but a save without a graph keeps the stored one.
    mergeEntry: mergeDeckMindmapEntry,
    entryKey: (e: SyncDeckMindmapEntry) => e.deckName,
    doPrefix: 'deckmindmap:',
    tsKeyPrefix: '__deckmindmap:',
    deleteType: 'deckMindmaps-delete',
    deleteBroadcastType: 'deckMindmaps-deleted',
    storage: { pk: ['deckName'] },
  }),

  // Professor Ka agentic-chat conversations. One entry per conversation,
  // keyed by `id`. `messages` holds the AI SDK message list verbatim and
  // stays opaque (`z.array(z.unknown())` — see the policy block above).
  // Personal (authority defaults to 'user'). Default LWW by `updatedAtISO`:
  // a conversation is single-user, so whole-record last-write-wins is enough.
  // Future fields MUST be `.optional()`.
  chatConversations: defineEntity({
    name: 'chatConversations',
    type: 'record',
    schema: z.object({
      id: z.string(),
      title: z.string(),
      messages: z.array(z.unknown()),
      deckIds: z.array(z.string()).optional(),
      createdTimeISO: z.string(),
      updatedAtISO: z.string(),
      deletedTimeISO: z.string().optional(),
    }) as unknown as z.ZodType<SyncChatConversationEntry>,
    entryKey: (e: SyncChatConversationEntry) => e.id,
    doPrefix: 'chatconv:',
    tsKeyPrefix: '__chatconv:',
    deleteType: 'chatConversations-delete',
    deleteBroadcastType: 'chatConversations-deleted',
    storage: { pk: ['id'] },
  }),

  // The set of study groups this user belongs to, with a dashboard-ready
  // cached summary (name, counts) populated by the dashboard batch HTTP.
  // Shared-group content (members, decks, cards, folders) lives in each
  // group's StudyGroupDo — this collection is the discovery index.
  groupMemberships: defineEntity({
    name: 'groupMemberships',
    type: 'collection',
    primaryKey: 'groupId',
    schema: z
      .object({
        groupId: z.string(),
        role: z.enum(['owner', 'editor', 'member']),
        joinedAtISO: z.string(),
        inviteToken: z.string(),
        lastOpenedAtISO: z.string().optional(),
        lastReadAtISO: z.string().optional(),
        cachedSummary: z
          .object({
            name: z.string(),
            icon: z.string().optional(),
            color: z.string().optional(),
            memberCount: z.number().optional(),
            deckCount: z.number().optional(),
            cardCount: z.number().optional(),
            lastActivityAtISO: z.string().optional(),
            // Additive-optional (post-deploy rule): older clients that
            // don't emit it still validate. Drives the tile avatar stack.
            members: z
              .array(
                z
                  .object({
                    userId: z.string(),
                    displayName: z.string().optional(),
                    avatarUrl: z.string().optional(),
                    lastOpenedAtISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .optional(),
            // Additive-optional: the tile's folder-art preview stack.
            previews: z
              .array(
                z
                  .object({
                    image: z.string().optional(),
                    emoji: z.string().optional(),
                    color: z.string().optional(),
                    folderColor: z.string().optional(),
                  })
                  .passthrough(),
              )
              .optional(),
          })
          .passthrough()
          .optional(),
        updatedAtISO: z.string().optional(),
        deletedTimeISO: z.string().nullable().optional(),
      })
      .passthrough() as z.ZodType<GroupMembership>,
  }),
  deckProperties: defineEntity({
    name: 'deckProperties',
    type: 'record',
    schema: z
      .object({
        // Identity / addressing. `deckPaths` is optional — the SG-side
        // payload (`sg-deckProperties-push`) addresses by `id` alone
        // and historically omits the legacy path-based field. UserDo
        // writes still include both.
        id: z.string(),
        deckPaths: z.array(z.string()).optional(),
        displayName: z.string().optional(),
        imageBackground: z.string().optional(),
        folder: z.string().optional(),
        color: z.string().optional(),
        thumbnailText: z.string().optional(),
        languageSettings: z
          .object({
            front: z.string().optional(),
            back: z.string().optional(),
          })
          .passthrough()
          .optional(),

        // Lifecycle timestamps.
        deletedTimeISO: z.string().optional(),
        // Manual archive marker. Nullable so unarchive can explicitly clear
        // by writing `null` on the wire (matches the sibling `archivedTimeISO`
        // pattern on folders).
        manualArchivedAtISO: z.string().nullable().optional(),
        // "Exclude from To-do" opt-out (personal scope; see DECK_FIELD_SCOPES).
        // A plain boolean: `false` survives the wire, so no nullable dance.
        // The ONLY stored state for the feature — a folder's To-do state is
        // derived from its decks (`dashboard/todo/exclusion.ts`), never stored,
        // so a folder can't outvote a deck the user just switched back on.
        excludedFromTodo: z.boolean().optional(),

        // Per-deck exam study plan (personal scope; see DECK_FIELD_SCOPES).
        // Nullable so a plan reset clears explicitly on the wire (same
        // pattern as manualArchivedAtISO). Loose passthrough shape — the
        // CLIENT owns the plan format (flashka-web exam-plan.ts) and a
        // future plan version must not fail wire validation; only the
        // addressing essentials are pinned.
        examPlan: z
          .object({
            version: z.number(),
            config: z.object({ examDateISO: z.string() }).passthrough(),
          })
          .passthrough()
          .nullable()
          .optional(),
        // Progress-reset floor (personal scope; see SyncDeckMeta).
        progressResetAtISO: z.string().optional(),
        // Twin merge record, written by the server (see SyncDeckMeta).
        sgMerged: z
          .object({
            intoDeckId: z.string(),
            groupId: z.string(),
            sgDeckId: z.string(),
            deckPaths: z.array(z.string()),
            linkedGroups: z.array(z.object({}).passthrough()),
            atISO: z.string(),
          })
          .passthrough()
          .optional(),
        addedTimeISO: z.string().optional(),
        updatedAtISO: z.string().optional(),

        // Total card count — kept on the meta so it's available even when the
        // deck is archived (cold) and its cards aren't on the device.
        cardCount: z.number().optional(),

        // Legacy personal-scope ordering (kept for in-flight writes;
        // cleaned up at the Phase 6 final cutover).
        orderIndex: z.number().optional(),
        sortKey: z.string().optional(),

        // Option F link metadata.
        linkedGroups: z
          .array(
            z
              .object({
                groupId: z.string(),
                sgDeckId: z.string(),
                linkedAtISO: z.string(),
                editPermission: z.enum(['owner-only', 'specific', 'everyone']),
                role: z.enum(['owner', 'member']),
              })
              .passthrough(),
          )
          .optional(),
        originDeckId: z.string().optional(),
        originUserId: z.string().optional(),
        forkedFromDeckId: z.string().optional(),

        // Phase 2 split-canonical personal-scope fields.
        personalFolderId: z.string().optional(),
        personalDeckSortKey: z.string().optional(),
        personalUpdatedAtISO: z.string().optional(),

        // Phase 2 split-canonical shared-scope fields.
        sgFolderId: z.string().optional(),
        sgDeckSortKey: z.string().optional(),
        editPermission: z.enum(['owner-only', 'specific', 'everyone']).optional(),
        editors: z.array(z.string()).optional(),
        addedByUserId: z.string().optional(),
        sharedAtISO: z.string().optional(),
        sharedUpdatedAtISO: z.string().optional(),

        // PDF / source overview cache. Array shape is forward-looking for
        // multi-source decks; today exactly one entry is written by the
        // lazy backfill in `useGetSource`. The heavy
        // `text_extraction_result` blob is intentionally NOT carried here.
        sources: z.array(deckSourceSchema).optional(),

        // Set when the user removed the LAST source. `sources: []` is
        // otherwise indistinguishable from "never backfilled", and the
        // backfill would put the removed document straight back.
        sourcesClearedAtISO: z.string().optional(),

        // Removed documents (`sourceId` → removedAtISO). `sources` is
        // unioned by id in `mergeEntry`, so a removal must be recorded or a
        // stale copy restores it. Additive.
        sourceTombstones: z.record(z.string()).optional(),

        // When each document was last made the first one (`sourceId` →
        // ISO). The merge puts the listed document with the latest stamp at
        // index 0, so a copy's order alone cannot move the primary. Additive.
        sourcePrimaryStamps: z.record(z.string()).optional(),

        // The deck's notes and mind maps — ids, names, and which source each
        // was generated from. The CONTENT stays where it is (a note in its own
        // Y.Doc, a map in `deckMindmaps`); this is the index that makes them
        // discoverable on a second device at all. Additive and optional: an
        // older client ignores it and keeps reading the deck's one note and
        // one map, which are the first entry of each list.
        //
        // Merged PER ENTRY, not as one field (`mergeEntry` below): every
        // entry carries its own `updatedAtISO` clock and a removal is a
        // `deletedAtISO` tombstone that stays in the list, so two rows
        // meeting anywhere union by id without losing a note.
        materials: z
          .object({
            notes: z
              .array(
                z
                  .object({
                    noteId: z.string(),
                    title: z.string().optional(),
                    addedAtISO: z.string(),
                    fromSourceId: z.string().optional(),
                    sortKey: z.string().optional(),
                    movedAtISO: z.string().optional(),
                    updatedAtISO: z.string().optional(),
                    deletedAtISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .optional(),
            mindmaps: z
              .array(
                z
                  .object({
                    mindmapId: z.string(),
                    title: z.string().optional(),
                    addedAtISO: z.string(),
                    fromSourceId: z.string().optional(),
                    sortKey: z.string().optional(),
                    movedAtISO: z.string().optional(),
                    updatedAtISO: z.string().optional(),
                    deletedAtISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .optional(),
          })
          .passthrough()
          .optional(),

        // Link-sharing (`/d/:code`). Personal-scope (see DECK_FIELD_SCOPES).
        shareCode: z.string().optional(),
        linkSharedAtISO: z.string().optional(),
        shareOptions: z
          .object({
            sharePdf: z.boolean().optional(),
            shareAiHelps: z.boolean().optional(),
            shareNotes: z.boolean().optional(),
            notesSnapshot: z.any().optional(),
            // Every note of a multi-note Set, captured at share time. Additive
            // (an older client's `.passthrough()` keeps it untouched).
            notesSnapshots: z
              .array(z.object({ noteId: z.string(), content: z.any() }).passthrough())
              .optional(),
            // Notes left off the row by the snapshot size budget — frozen by
            // the owner's DO when it receives the share. Additive.
            liveNoteIds: z.array(z.string()).optional(),
            // Content fingerprint of those notes, so an edit + re-share is a
            // changed row and re-freezes them. Additive.
            liveNotesRev: z.string().optional(),
          })
          .passthrough()
          .optional(),
        sharedByUserId: z.string().optional(),
      })
      // Double-cast via `unknown` because `id` / `deckPaths` carry branded
      // types (`DeckId`, `DeckPath`) at the TS level. Zod can't see brands —
      // they're erased at runtime — so the validator emits raw `string`. The
      // cast asserts "trust me, the brand holds at every call site that
      // produces a SyncDeckMeta". Matches the assertion at every brand
      // boundary in the engine.
      .passthrough() as unknown as z.ZodType<SyncDeckMeta>,
    entryKey: (m: SyncDeckMeta) => m.id as string,
    // Ingest guard (drop-only, no healing): UserDo storage can hold
    // malformed rows — pre-5.3b `DODeckMeta` envelopes or id-less minimal
    // metas — and the outbound diff path ships rows unvalidated. An id-less
    // row in the store keys as `deckProperties["undefined"]`, hides the
    // deck from every deck list, and crashes id-reading consumers. Dropping
    // records no sync timestamp, so a future server-side repair could still
    // redeliver the row.
    normalizeServerEntry: (key, entry) => {
      if (isValidDeckMetaRow(entry)) return entry;
      console.warn(`[sync] dropping malformed deckProperties row for key ${key}`);
      return null;
    },
    doPrefix: 'deckmeta\x01',
    tsKeyPrefix: '__deckmeta\x01',
    deleteType: 'deckProperties-delete',
    deleteBroadcastType: 'deckProperties-deleted',
    storage: { pk: ['id'] },
    fieldScopes: DECK_FIELD_SCOPES,
    // Row-level LWW exactly as the default (ties to incoming), but the
    // documents list (`sources`, with `sourceTombstones`) and the
    // notes/maps index are UNIONED by entry — the index is the only path to
    // a note's content, so a whole-row loss would orphan what the user
    // wrote. Runs on the UserDo and StudyGroupDo push paths, on the
    // client's reconnect diff (`applySyncDiff`) and — because
    // `foldBroadcasts` is declared — on the client's live broadcast apply
    // too: a peer's row folds into this device's in-flight one instead of
    // replacing it. The fold is safe HERE because every notes/maps entry
    // carries its own clock; it is the only entity that declares it (see
    // `RecordEntityInput.foldBroadcasts` for why quizProgress must not).
    mergeEntry: mergeDeckMetaEntry,
    foldBroadcasts: true,

    // ── Split-canonical declaration ──────────────────────────────────
    //
    // `deckProperties` lives in BOTH UserDo (canonical for personal
    // fields + shared snapshot) and StudyGroupDo (canonical for shared
    // fields on linked decks). The field-scope router partitions every
    // write: personal half stays in UserDo, shared half fans out to
    // each linked SG, then back to other members' UserDos.
    //
    // The SG-side dispatch is fully framework-driven — the per-row
    // record wires (`sg-deckProperties-push` / `sg-deckProperties-updated`)
    // declared below are the only emitters; storage is unwrapped
    // (`sgdeckmeta\x01{id}` rows ARE the SGDeckProps payload, no
    // outer envelope).
    authority: 'both',
    mirror: 'split',
    groupStorageKey: 'sgdeckmeta',
    groupMessages: {
      push: {
        type: 'sg-deckProperties-push',
        dataKey: 'sgDeckProperties',
      },
      broadcast: {
        type: 'sg-deckProperties-updated',
        dataKey: 'sgDeckProperties',
      },
    },
    groupDeleteType: 'sg-deckProperties-delete',
    groupDeleteBroadcastType: 'sg-deckProperties-deleted',
  }),

  cards: defineEntity({
    name: 'cards',
    type: 'record',
    // Top-level fields validated; nested unions stay opaque.
    // `actions[]` (CardAction discriminated-by-key), `studyState[]`
    // (CardStudyState w/ SRAlgoInfo union), `chat[]` (SyncChatMessage
    // w/ Date|string `createdAt`), `aiNotes[]`, `images`, `tags[]`
    // are all typed as `z.unknown()` / `z.array(z.unknown())` per the
    // policy block above.
    //
    // `deckId` is OPTIONAL because the UserDo client wire encodes the
    // deck via the compound key (`deckId\x01cardId`) and does not
    // duplicate it in the entry body (see `flattenNested` in
    // define-sync-entity.ts). The SG-side `handleDeckPush` does stamp
    // `deckId` onto the entry from `msg.deckId` — both shapes must
    // pass validation.
    //
    // Future fields MUST be `.optional()`.
    schema: z.object({
      id: z.string(),
      deckId: z.string().optional(),
      // Legacy migration tolerance: some old cards stored these text fields
      // as arrays (notably `notes: []`). Coerce array→string so a stale shape
      // doesn't fail `cards-bulk-push` validation. See coerceLegacyCardString.
      front: z.preprocess(coerceLegacyCardString, z.string()),
      back: z.preprocess(coerceLegacyCardString, z.string().optional()),
      source: z.preprocess(coerceLegacyCardString, z.string().optional()),
      notes: z.preprocess(coerceLegacyCardString, z.string().optional()),
      aiNotes: z.array(z.unknown()).optional(),
      createdTimeISO: z.string(),
      deletedTimeISO: z.string().nullable().optional(),
      updatedAtISO: z.string().optional(),
      sharedUpdatedAtISO: z.string().optional(),
      personalUpdatedAtISO: z.string().optional(),
      // Personal-scope per CARD_FIELD_SCOPES — stripped by the SG-side
      // projection. Optional on the wire so `sg-cards-push` (which
      // re-emits projected SG cards via `flushLocalDirty`) passes
      // validation. Client-side `cleanCardForStore` + `cleanHydratedCard`
      // backfill these to `[]` before they reach Zustand, so direct
      // readers (`card.actions.length`, `card.studyState.length`) keep
      // seeing arrays — the wire-level looseness is an asymmetry
      // contained to the SG → UserDo projection boundary.
      actions: z.array(z.unknown()).optional(),
      studyState: z.array(z.unknown()).optional(),
      chat: z.array(z.unknown()).optional(),
      images: z.unknown().optional(),
      tags: z.array(z.unknown()).optional(),
      starred: z.boolean().optional(),
      joinedDeckAtISO: z.string().optional(),
      undoneISO: z.array(z.string()).optional(),
      sortKey: z.preprocess(coerceLegacyCardString, z.string().optional()),
      sourceTimeSec: z.number().optional(),
      sourceTimeEndSec: z.number().optional(),
    }) as unknown as z.ZodType<SyncCardProps>,
    entryKey: (c: SyncCardProps) =>
      `${(c as SyncCardProps & { deckId?: string }).deckId ?? ''}${CARD_KEY_DELIM}${c.id}`,
    doPrefix: `card${CARD_KEY_DELIM}`,
    tsKeyPrefix: `__card${CARD_KEY_DELIM}`,
    deleteType: 'cards-delete',
    deleteBroadcastType: 'cards-deleted',
    storage: { pk: ['deckId', 'id'] },
    // Compound entry key `deckId\x01cardId` → in-memory state is
    // `Record<deckId, Record<cardId, Card>>`. Keeps `cards[deckId]` O(1)
    // and preserves per-deck reference equality for Zustand selectors.
    nested: true,
    fieldScopes: CARD_FIELD_SCOPES,
    // Review history only grows unless a removal is explicit (`undoneISO`);
    // every other field is whole-row LWW as before. A join, so the client
    // folds broadcasts with it too — see `card-history-join.ts`.
    mergeEntry: joinCardHistory,
    foldBroadcasts: true,

    // ── Split-canonical declaration ──────────────────────────────────
    //
    // Cards live in BOTH UserDo (canonical for personal fields like
    // `starred`, `studyState`, `notes`, `tags`, `actions`, `chat`) and
    // StudyGroupDo (canonical for shared fields like `front`, `back`,
    // `aiNotes`, `images`, `source` on linked decks). The framework
    // projects each push by `CARD_FIELD_SCOPES` at the storage
    // boundary so the SG row can never carry personal data.
    //
    // SG-side dispatch is framework-driven: per-row `sg-cards-push`
    // envelopes from the client, per-row `sg-cards-updated`
    // broadcasts to other members. The compound `sg-deck-push`
    // (initial deck share = meta + cards together) keeps its bespoke
    // handler since it ships both halves atomically; every subsequent
    // card edit flows through the personal store and back via
    // `propagateSharedFieldsToLinkedGroups` → `handleInternalApplySharedFieldEdit`.
    authority: 'both',
    mirror: 'split',
    groupStorageKey: 'sgcard',
    groupMessages: {
      push: { type: 'sg-cards-push', dataKey: 'sgCards' },
      broadcast: { type: 'sg-cards-updated', dataKey: 'sgCards' },
    },
    groupDeleteType: 'sg-cards-delete',
    groupDeleteBroadcastType: 'sg-cards-deleted',
  }),

  // ─── StudyGroupDo entities (Phase A) ─────────────────────────────────
  //
  // Moved here from the now-retired `groupEntitySchema` in
  // `./group-entities.ts`. Wire types, storage keys, and message
  // names are preserved verbatim by the explicit `messages` overrides
  // — Phase A is a registry-consolidation refactor, not a protocol
  // change. The `authority: 'group'` tag is the only new metadata;
  // `framework/group-entity-registry.ts` filters by it so only
  // StudyGroupDo dispatches these.
  //
  // Each entity's per-row LWW gates and authorization rules are
  // unchanged. Adding a new group-side entity = one `defineEntity`
  // call here with `authority: 'group'` — no other wiring needed.

  // Group identity. Single value; owner-only writes enforced server-side.
  //
  // `name: "sgMeta"` drives stateKey/tsKey/storageKey defaults so the
  // sync-init diff emits `msg.sgMeta` (the client's applySyncDiff reads
  // that key). A previous `name: "meta"` mismatched the client's
  // `msg.sgMeta` lookup and meta never landed on first connect —
  // manifesting as "Group not found" right after creating a group.
  sgMeta: defineEntity({
    name: 'sgMeta',
    authority: 'group',
    type: 'value',
    // `.passthrough()` — preserve unknown fields for cross-version
    // forward-compat (see folders/groupMemberships above).
    schema: z
      .object({
        id: z.string(),
        name: z.string(),
        icon: z.string().nullable(),
        color: z.string().nullable(),
        createdBy: z.string(),
        inviteToken: z.string(),
        createdTimeISO: z.string(),
        updatedAtISO: z.string().optional(),
      })
      .passthrough() as z.ZodType<StudyGroupMeta>,
    initial: undefined as unknown as StudyGroupMeta,
    messages: {
      push: { type: 'sg-meta-push', dataKey: 'sgMeta' },
      broadcast: { type: 'sg-meta-updated', dataKey: 'sgMeta' },
    },
  }),

  // Members collection, per-row LWW. The owner may rewrite any row;
  // other members may write only their own non-role fields (enforced by
  // StudyGroupDo). Storage key `sgMembers`; primary key `userId`.
  sgMembers: defineEntity({
    name: 'sgMembers',
    authority: 'group',
    type: 'collection',
    primaryKey: 'userId',
    schema: z
      .object({
        userId: z.string(),
        role: z.enum(['owner', 'editor', 'member']),
        joinedAtISO: z.string(),
        lastOpenedAtISO: z.string().optional(),
        lastReadAtISO: z.string().optional(),
        displayName: z.string().optional(),
        avatarUrl: z.string().optional(),
        leaderboardOptOut: z.boolean().optional(),
        updatedAtISO: z.string().optional(),
        deletedTimeISO: z.string().nullable().optional(),
      })
      .passthrough() as z.ZodType<SGMember>,
    messages: {
      push: { type: 'sg-members-push', dataKey: 'sgMembers' },
      broadcast: { type: 'sg-members-updated', dataKey: 'sgMembers' },
    },
  }),

  // Per-member score row, one per (group × user). Pushed via the
  // custom `sg-score-delta` handler in StudyGroupDo — NOT the framework
  // record-push pipeline (the server intercepts the wire before
  // dispatch). This registration only exists so the client store has a
  // known slice + LWW merger keyed under `sgScores`; runtime per-card
  // flow is single-sender ack with no broadcast, and reads by other
  // members hydrate via REST `/leaderboard` (no Y.Doc replication).
  //
  // All MP fields are CENTI-MP INTEGERS — see SGScore type doc.
  sgScores: defineEntity({
    name: 'sgScores',
    authority: 'group',
    type: 'collection',
    primaryKey: 'userId',
    schema: z
      .object({
        userId: z.string(),
        totalMP: z.number().int().nonnegative().optional(),
        weekMP: z.number().int().nonnegative().optional(),
        weekStartISO: z.string().optional(),
        monthlyMP: z.record(z.string(), z.number().int().nonnegative()).optional(),
        todayMP: z.number().int().nonnegative().optional(),
        todayLocalDayISO: z.string().optional(),
        streak: z.number().int().nonnegative().optional(),
        lastStudyLocalDayISO: z.string().optional(),
        // Study-time buckets (integer ms) — the Time ranking siblings of
        // the centi-MP fields above. See SGScore type doc.
        totalTimeMs: z.number().int().nonnegative().optional(),
        weekTimeMs: z.number().int().nonnegative().optional(),
        monthlyTimeMs: z.record(z.string(), z.number().int().nonnegative()).optional(),
        todayTimeMs: z.number().int().nonnegative().optional(),
        updatedAtISO: z.string().optional(),
        deletedTimeISO: z.string().nullable().optional(),
      })
      .passthrough() as z.ZodType<SGScore>,
    messages: {
      // The custom handler in studygroupdo.ts intercepts `sg-score-delta`
      // and responds with a SENDER-ONLY ack of type `sg-score-ack` (via
      // sendCustomMessage; never broadcastAll). To prevent the framework's
      // auto-derived broadcast map from routing `sg-score-ack` into the
      // sgScores LWW merger (the ack payload is a thin scalar bundle, not
      // a full row), we register a sentinel broadcast type that is never
      // sent or received. The client facade in `sync-client/study-groups`
      // registers its own inbound handler for the real `sg-score-ack`
      // wire outside the framework registry.
      push: { type: 'sg-score-delta', dataKey: 'sgScores' },
      broadcast: { type: 'sg-score-broadcast-noop', dataKey: 'sgScores' },
    },
  }),

  // Folders collection; structurally similar to UserDo.folders but
  // lacks the `language` field (group folders are language-agnostic).
  sgFolders: defineEntity({
    name: 'sgFolders',
    authority: 'group',
    type: 'collection',
    primaryKey: 'id',
    schema: z
      .object({
        id: z.string(),
        name: z.string(),
        color: z.string(),
        // Nullable for the same reason as UserDo.folders.archivedTimeISO:
        // clients push `null` for non-archived folders.
        archivedTimeISO: z.string().nullable().optional(),
        parentFolder: z.string().nullable().optional(),
        orderIndex: z.number().optional(),
        sortKey: z.string().optional(),
        updatedAtISO: z.string().optional(),
        deletedTimeISO: z.string().nullable().optional(),
      })
      .passthrough() as z.ZodType<SGFolder>,
    messages: {
      push: { type: 'sg-folders-push', dataKey: 'sgFolders' },
      broadcast: { type: 'sg-folders-updated', dataKey: 'sgFolders' },
    },
  }),

  // NOTE: `deckProps` is no longer a top-level registered entity — props
  // live inlined inside `sgdeckmeta\x01*` rows and the collection is
  // derived on read. SG-side dispatch happens through the `deckProperties`
  // `mirror: 'split'` declaration above, which emits per-row
  // `sg-deckProperties-push` / `sg-deckProperties-updated` via the
  // framework's record-entity handlers.

  // Who may draw (pencil/shapes) on the group's PDF sources. Single
  // value; owner-only writes enforced in `server/group/authorize.ts`.
  // The binary-frame gate on `hw\x01*` doc keys reads this via
  // StudyGroupDo's draw-settings cache.
  sgDrawSettings: defineEntity({
    name: 'sgDrawSettings',
    authority: 'group',
    type: 'value',
    schema: z
      .object({
        mode: z.enum(['everyone', 'specific', 'owner-only']),
        allowedUserIds: z.array(z.string()),
        updatedAtISO: z.string().optional(),
      })
      .passthrough() as z.ZodType<SGDrawSettings>,
    initial: {
      mode: 'everyone',
      allowedUserIds: [],
    } as SGDrawSettings,
    messages: {
      push: { type: 'sg-drawsettings-push', dataKey: 'sgDrawSettings' },
      broadcast: { type: 'sg-drawsettings-updated', dataKey: 'sgDrawSettings' },
    },
  }),

  // Activity feed. Record entity so each entry has an independent
  // timestamp; storage prefix `sgactivity:`. StudyGroupDo trims on
  // append and on a daily alarm.
  sgActivity: defineEntity({
    name: 'sgActivity',
    authority: 'group',
    type: 'record',
    // `metadata` stays opaque (free-form per activity type). Future
    // fields MUST be `.optional()`.
    schema: z.object({
      id: z.string(),
      type: z.enum([
        'deck_added',
        'deck_removed',
        'deck_pushed',
        'member_joined',
        'member_left',
        'cards_studied',
        'quiz_completed',
        'group_updated',
        'leader_change',
        'weekly_recap',
      ]),
      userId: z.string(),
      metadata: z.record(z.unknown()).optional(),
      createdTimeISO: z.string(),
      updatedAtISO: z.string().optional(),
      deletedTimeISO: z.string().nullable().optional(),
    }) as unknown as z.ZodType<SGActivityEntry>,
    entryKey: (e: SGActivityEntry) => e.id,
    doPrefix: 'sgactivity:',
    tsKeyPrefix: '__sgactivity:',
    deleteType: 'sg-activity-delete',
    deleteBroadcastType: 'sg-activity-deleted',
    messages: {
      push: { type: 'sg-activity-push', dataKey: 'sgActivity' },
      broadcast: { type: 'sg-activity-updated', dataKey: 'sgActivity' },
    },
  }),

  // A shared deck's mind maps, so members can read them: the sharer's own
  // `deckMindmaps` entries, published per map under `${sgDeckId}\x01${mindmapId}`.
  // Only the sharer writes (`server/group/authorize.ts`); same merge as the
  // personal record. Future fields MUST be `.optional()`.
  sgDeckMindmaps: defineEntity({
    name: 'sgDeckMindmaps',
    authority: 'group',
    type: 'record',
    schema: z.object({
      key: z.string(),
      mindmap: z.object({
        title: z.string().optional(),
        nodes: z.array(z.unknown()),
      }),
      layout: z.unknown().optional(),
      graph: z.unknown().optional(),
      graphStale: z.literal(true).optional(),
      updatedAtISO: z.string(),
      deletedTimeISO: z.string().nullable().optional(),
    }) as unknown as z.ZodType<SGDeckMindmapEntry>,
    mergeEntry: mergeDeckMindmapEntry,
    entryKey: (e: SGDeckMindmapEntry) => e.key,
    doPrefix: 'sgdeckmindmap:',
    tsKeyPrefix: '__sgdeckmindmap:',
    deleteType: 'sg-deckMindmaps-delete',
    deleteBroadcastType: 'sg-deckMindmaps-deleted',
    messages: {
      push: { type: 'sg-deckMindmaps-push', dataKey: 'sgDeckMindmaps' },
      broadcast: { type: 'sg-deckMindmaps-updated', dataKey: 'sgDeckMindmaps' },
    },
  }),
} as const;

export type EntitySchema = typeof entitySchema;

// ---------------------------------------------------------------------------
// Inferred types — extracted from the schema, not hand-written
// ---------------------------------------------------------------------------

export type SyncFolder = typeof entitySchema.folders.T;
export type SpacedRepetitionSettings = typeof entitySchema.settings.T;
export type SyncUserInfo = typeof entitySchema.userInfo.T;
export type SyncSocialConsent = typeof entitySchema.socialConsent.T;
export { DEFAULT_NOTIFICATIONS };
// Re-exported from the explicit type above (z.custom can't infer the branded shape).
export type { GroupMembership as SyncGroupMembership };
